import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Offcanvas, OffcanvasBody, Input, Spinner } from "reactstrap";
import moment from "moment";
import "moment/locale/mn";
import dummyImg from "../../assets/images/users/user-dummy-img.jpg";
import "./ChatDrawer.css";
import EmojiPicker from "emoji-picker-react";
import ChatAttachment, { humanSize } from "./ChatAttachment";
import ChatText from "./ChatText";
import {
  getChatContacts,
  getChatMessages,
  sendChatMessageWith,
  deleteChatMessage,
  uploadChatFiles,
  forwardChatMessage,
  reactChatMessage,
  getChatGroups,
  getChatGroupMessages,
  sendChatGroupMessage,
  clearChatGroup,
} from "../../helpers/chat_helper";

// Зөвшөөрөгдсөн хариу үйлдэл — сервэр талын жагсаалттай ИЖИЛ байх ёстой
const REACTIONS = ["❤️", "👍", "😂", "😮", "😢", "🙏"];

moment.locale("mn");

// Socket.io энэ төсөлд байхгүй тул шинэ мессежийг ТОГТМОЛ АСУУЛТААР (polling)
// авна. Хоёр өөр давтамж: нээлттэй яриа хурдан, харилцагчийн жагсаалт удаан —
// ингэснээр сервэрт унах ачаалал бага байна.
const POLL_CONVO_MS = 4000;
const POLL_LIST_MS = 12000;

// ⚠️ `api_helper`-ийн interceptor нь алдааг МӨР болгож reject хийдэг
// (Error объект БИШ). Тиймээс `e.message`/`e.status` уншиж болохгүй.
const errText = (e, fallback) => {
  const s = typeof e === "string" ? e : (e && e.message) || "";
  return s || fallback;
};
const isForbidden = (e) => /\b403\b|Зөвшөөрөгдөхгүй/.test(String(typeof e === "string" ? e : (e && e.message) || ""));

// Группд олон хүн бичдэг тул нэр бүрд ТОГТВОРТОЙ өнгө оноодог
// (id-гаас тооцно — дахин ачаалахад өнгө нь өөрчлөгдөхгүй).
const NAME_COLORS = [
  "#0ab39c", "#f06548", "#405189", "#f7b84b", "#299cdb",
  "#6559cc", "#e83e8c", "#20c997", "#fd7e14", "#4b38b3",
];
const authorColor = (id) => {
  const s = String(id || "");
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return NAME_COLORS[h % NAME_COLORS.length];
};

const fmtTime = (d) => moment(d).format("HH:mm");
const fmtDay = (d) => {
  const m = moment(d);
  if (m.isSame(moment(), "day")) return "Өнөөдөр";
  if (m.isSame(moment().subtract(1, "day"), "day")) return "Өчигдөр";
  return m.format("YYYY.MM.DD");
};
// Жагсаалт дахь товч цаг: өнөөдөр бол цаг, эс бол огноо
const fmtShort = (d) => {
  const m = moment(d);
  return m.isSame(moment(), "day") ? m.format("HH:mm") : m.format("MM/DD");
};

const Avatar = ({ user, size = "avatar-sm", showDot = true }) => (
  user.isGroup ? (
    <div className={`position-relative flex-shrink-0 rounded-circle bg-soft-primary text-primary d-flex align-items-center justify-content-center ${size}`}
      style={{ width: 36, height: 36 }}>
      <i className={`${user.icon || "ri-group-line"} fs-18`}></i>
      {user.memberCount ? (
        <span className="position-absolute badge bg-primary rounded-pill"
          style={{ fontSize: 8, bottom: -3, right: -6, padding: "1px 4px" }}>
          {user.memberCount}
        </span>
      ) : null}
    </div>
  ) : (
  <div className="position-relative flex-shrink-0">
    <img
      src={user.image || dummyImg}
      alt={user.name}
      className={`rounded-circle ${size}`}
      style={{ objectFit: "cover" }}
      onError={(e) => { e.target.onerror = null; e.target.src = dummyImg; }}
    />
    {showDot && (
      <span
        className={`position-absolute rounded-circle border border-2 border-white ${user.online ? "bg-success" : "bg-secondary"}`}
        style={{ width: 10, height: 10, right: 0, bottom: 0 }}
        title={user.online ? "Онлайн" : user.lastSeen ? `Сүүлд: ${moment(user.lastSeen).fromNow()}` : "Офлайн"}
      />
    )}
  </div>
  )
);

const ChatDrawer = ({ isOpen, toggle, onUnreadChange }) => {
  const [contacts, setContacts] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [q, setQ] = useState("");

  const [active, setActive] = useState(null);        // сонгосон харилцагч
  const [convo, setConvo] = useState({ user: null, messages: [], hasMore: false });
  const [loadingConvo, setLoadingConvo] = useState(false);
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  // Хавсралт: илгээхээс өмнө хүлээж буй файлууд
  const [pending, setPending] = useState([]);   // File[]
  const [uploading, setUploading] = useState(false);
  const [showEmoji, setShowEmoji] = useState(false);
  const [preview, setPreview] = useState(null); // томруулж харах зураг
  const fileRef = useRef(null);

  // Мессеж дээр дарахад гарах үйлдлийн хуудас + дамжуулах сонгогч
  const [acting, setActing] = useState(null);      // сонгосон мессеж
  const [fwdFor, setFwdFor] = useState(null);      // дамжуулах мессеж
  const [fwdPick, setFwdPick] = useState([]);      // сонгосон хүлээн авагчид
  const [fwdQ, setFwdQ] = useState("");
  const [fwdBusy, setFwdBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [replyTo, setReplyTo] = useState(null);    // эш татаж буй мессеж

  // Группын түүхийг цэвэрлэх (зөвхөн SuperAdmin). Сервэр ч эрхийг шалгана —
  // энэ нь зөвхөн товчийг нуух зорилготой.
  const isSuperAdmin = useMemo(() => {
    try { return (JSON.parse(localStorage.getItem("authUser")) || {}).role === "SuperAdmin"; }
    catch (e) { return false; }
  }, []);
  const [confirmClear, setConfirmClear] = useState(false);
  const [clearing, setClearing] = useState(false);

  const doClear = async () => {
    if (!active || !active.isGroup || clearing) return;
    setClearing(true);
    try {
      const r = await clearChatGroup(active._id);
      setConvo((c) => ({ ...c, messages: [] }));
      setToast(`${r.deleted} мессеж устлаа`);
      loadContacts(true);
    } catch (e) {
      setError(errText(e, "Цэвэрлэж чадсангүй."));
    } finally {
      setClearing(false);
      setConfirmClear(false);
      setTimeout(() => setToast(""), 2500);
    }
  };

  const bodyRef = useRef(null);
  const endRef = useRef(null);
  // Хэрэглэгч дээшээ гүйлгэж хуучныг уншиж байвал шинэ мессеж ирэхэд
  // хүчээр доош үсрэхгүй байх
  const stickBottom = useRef(true);
  // Илгээлт явж буй эсэх — polling-д харагдах ёстой тул ref (state биш)
  const sendingRef = useRef(false);

  /* ── Харилцагчид ── */
  const loadContacts = useCallback(async (silent) => {
    if (!silent) setLoadingList(true);
    try {
      // Групп болон 1:1-ийг ЗЭРЭГ уншиж, нэг жагсаалт болгоно.
      // Группыг дээр нь байрлуулна — багийн яриа ихэвчлэн илүү идэвхтэй.
      const [people, groups] = await Promise.all([
        getChatContacts(),
        getChatGroups().catch(() => []),
      ]);
      const p = Array.isArray(people) ? people : [];
      const g = Array.isArray(groups) ? groups : [];
      setContacts([...g, ...p]);
      if (onUnreadChange) {
        onUnreadChange([...g, ...p].reduce((s, r) => s + (r.unread || 0), 0));
      }
    } catch (e) {
      if (!silent) setError(errText(e, "Харилцагчдыг ачаалж чадсангүй."));
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [onUnreadChange]);

  /* ── Нэг яриа ── */
  const loadConvo = useCallback(async (target, silent) => {
    if (!silent) setLoadingConvo(true);
    try {
      const isGroup = typeof target === "object" ? target.isGroup : false;
      const id = typeof target === "object" ? target._id : target;
      const r = isGroup ? await getChatGroupMessages(id) : await getChatMessages(id);
      // Групп ба 1:1-ийг НЭГ хэлбэрт оруулна — доорх зурах код нэг л байна
      setConvo(
        r
          ? { user: r.group || r.user, messages: r.messages || [], hasMore: r.hasMore, isGroup }
          : { user: null, messages: [], hasMore: false }
      );
      setError("");
    } catch (e) {
      // Эрхгүй бол (сервэр шийднэ) жагсаалт хуучирсан байж болзошгүй тул сэргээнэ
      setError(errText(e, "Яриаг ачаалж чадсангүй."));
      if (isForbidden(e)) { setActive(null); loadContacts(true); }
    } finally {
      if (!silent) setLoadingConvo(false);
    }
  }, [loadContacts]);

  // Нээгдэхэд болон жагсаалт руу буцахад
  useEffect(() => {
    if (!isOpen) return;
    if (active) loadConvo(active);
    else loadContacts();
  }, [isOpen, active, loadConvo, loadContacts]);

  // Тогтмол шалгалт — цонх нээлттэй байхад л
  useEffect(() => {
    if (!isOpen) return undefined;
    const ms = active ? POLL_CONVO_MS : POLL_LIST_MS;
    const t = setInterval(() => {
      if (document.hidden) return;                 // таб идэвхгүй бол дэмий асуухгүй
      // ⚠️ Илгээж байх зуур яриаг дахин уншвал сервэрийн жагсаалт нь түр
      // зуурын (optimistic) мессежийг дарж, хариу ирэхэд түүнийг олдохгүй
      // болгоно — мессеж хэсэг хугацаанд АЛГА болж харагдана.
      if (sendingRef.current) return;
      if (active) loadConvo(active, true);
      else loadContacts(true);
    }, ms);
    return () => clearInterval(t);
  }, [isOpen, active, loadConvo, loadContacts]);

  // Шинэ мессеж ирэхэд доош гүйлгэнэ
  useEffect(() => {
    if (stickBottom.current && endRef.current) {
      endRef.current.scrollIntoView({ block: "end" });
    }
  }, [convo.messages]);

  const onScroll = () => {
    const el = bodyRef.current;
    if (!el) return;
    stickBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 60;
  };

  const openConvo = (c) => {
    stickBottom.current = true;
    setConvo({ user: null, messages: [], hasMore: false });
    setActive(c);
    // Тухайн хүний уншаагүйн тэмдгийг шууд арилгана (сервэр ч тэмдэглэнэ)
    setContacts((list) => list.map((x) => (x._id === c._id ? { ...x, unread: 0 } : x)));
    if (onUnreadChange) {
      onUnreadChange(contacts.reduce((s, r) => s + (r._id === c._id ? 0 : r.unread || 0), 0));
    }
  };

  const send = async () => {
    const text = draft.trim();
    const files = pending;
    const quoted = replyTo;
    if ((!text && !files.length) || !active || sending) return;
    sendingRef.current = true;
    setSending(true);
    setShowEmoji(false);
    setDraft("");
    setPending([]);
    setReplyTo(null);
    stickBottom.current = true;
    // Түр зуурын мессеж — сүлжээ хүлээхгүйгээр шууд харагдана
    const tmp = {
      _id: `tmp-${Date.now()}`, text, mine: true, at: new Date().toISOString(),
      pending: true,
      reply: quoted
        ? {
            author: quoted.mine ? "Та" : (convo.user || active).name,
            text: quoted.text || "",
            hasFile: Boolean((quoted.attachments || []).length),
          }
        : null,
      // Илгээгч өөрийн зургаа ШУУД харна — сервэрээс буцааж татахыг хүлээхгүй
      attachments: files.map((f) => ({
        name: f.name,
        size: f.size,
        kind: /^image\//.test(f.type) ? "image" : "file",
        localUrl: /^image\//.test(f.type) ? URL.createObjectURL(f) : null,
      })),
    };
    setConvo((c) => ({ ...c, messages: [...c.messages, tmp] }));
    try {
      let attachments = [];
      if (files.length) {
        setUploading(true);
        attachments = await uploadChatFiles(files);
        setUploading(false);
      }
      const saved = active.isGroup
        ? await sendChatGroupMessage(active._id, text, attachments, quoted ? quoted._id : null)
        : await sendChatMessageWith(active._id, text, attachments, quoted ? quoted._id : null);
      // Локал object URL-уудыг үлдээвэл санах ой алдагдана
      tmp.attachments.forEach((a) => { if (a.localUrl) URL.revokeObjectURL(a.localUrl); });
      setConvo((c) => {
        const has = c.messages.some((m) => m._id === tmp._id);
        return {
          ...c,
          // tmp нь polling-д дарагдсан байвал шинэ мессежийг ХАЯХГҮЙ, нэмнэ
          messages: has
            ? c.messages.map((m) => (m._id === tmp._id ? saved : m))
            : c.messages.some((m) => String(m._id) === String(saved._id))
              ? c.messages
              : [...c.messages, saved],
        };
      });
    } catch (e) {
      setUploading(false);
      // Илгээгдээгүйг ЧИМЭЭГҮЙ алга болгохгүй — тэмдэглээд эргүүлж бичих боломж өгнө
      setConvo((c) => ({
        ...c,
        messages: c.messages.map((m) => (m._id === tmp._id ? { ...m, failed: true, pending: false } : m)),
      }));
      setError(errText(e, "Илгээж чадсангүй."));
    } finally {
      sendingRef.current = false;
      setSending(false);
    }
  };

  const react = async (msg, emoji) => {
    // Хариуг хүлээхгүйгээр шууд харуулна — дарсан мэдрэмж хурдан байх ёстой
    setConvo((c) => ({
      ...c,
      messages: c.messages.map((m) => {
        if (m._id !== msg._id) return m;
        const cur = m.reactions || [];
        const same = cur.find((r) => r.emoji === emoji && r.mine);
        const dropped = cur
          .map((r) => (r.mine ? { ...r, count: r.count - 1, mine: false } : r))
          .filter((r) => r.count > 0);
        if (same) return { ...m, reactions: dropped };
        const hit = dropped.find((r) => r.emoji === emoji);
        return {
          ...m,
          reactions: hit
            ? dropped.map((r) => (r.emoji === emoji ? { ...r, count: r.count + 1, mine: true } : r))
            : [...dropped, { emoji, count: 1, mine: true, names: [] }],
        };
      }),
    }));
    try {
      const r = await reactChatMessage(msg._id, emoji);
      setConvo((c) => ({
        ...c,
        messages: c.messages.map((m) => (m._id === msg._id ? { ...m, reactions: r.reactions } : m)),
      }));
    } catch (e) {
      setError(errText(e, "Тэмдэг тавьж чадсангүй."));
      if (active) loadConvo(active, true);
    }
  };

  const copyText = async (t) => {
    try {
      await navigator.clipboard.writeText(t);
      setToast("Хуулагдлаа");
    } catch (e) {
      setToast("Хуулж чадсангүй");
    }
    setTimeout(() => setToast(""), 2000);
  };

  const doForward = async () => {
    if (!fwdFor || !fwdPick.length || fwdBusy) return;
    setFwdBusy(true);
    try {
      const r = await forwardChatMessage(fwdFor._id, fwdPick);
      setToast(`${(r && r.count) || fwdPick.length} хүнд дамжуулав`);
      setFwdFor(null);
      setFwdPick([]);
      setFwdQ("");
      // Өөр рүүгээ дамжуулсан бол нээлттэй яриа шинэчлэгдэнэ
      if (active && fwdPick.includes(String(active._id))) loadConvo(active, true);
      loadContacts(true);
    } catch (e) {
      setError(errText(e, "Дамжуулж чадсангүй."));
    } finally {
      setFwdBusy(false);
      setTimeout(() => setToast(""), 2500);
    }
  };

  const removeMessage = async (id) => {
    setConvo((c) => ({ ...c, messages: c.messages.filter((m) => m._id !== id) }));
    try { await deleteChatMessage(id); } catch (e) { loadConvo(active, true); }
  };

  const onKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); }
  };

  const MAX_MB = 15;
  const addFiles = (list) => {
    const arr = [...list];
    const tooBig = arr.filter((f) => f.size > MAX_MB * 1024 * 1024);
    if (tooBig.length) {
      setError(`${tooBig.map((f) => f.name).join(", ")} — ${MAX_MB}MB-аас том тул хасагдлаа.`);
    }
    const good = arr.filter((f) => f.size <= MAX_MB * 1024 * 1024);
    setPending((p) => [...p, ...good].slice(0, 10));
  };

  // Хавсралтыг хуулж наах (Ctrl+V) — зураг илгээхэд хамгийн хурдан арга
  const onPaste = (e) => {
    const files = [...(e.clipboardData && e.clipboardData.files ? e.clipboardData.files : [])];
    if (files.length) { e.preventDefault(); addFiles(files); }
  };

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return contacts;
    return contacts.filter(
      (c) => String(c.name).toLowerCase().includes(s) || String(c.company).toLowerCase().includes(s)
    );
  }, [contacts, q]);

  // Өдрөөр нь бүлэглэсэн мессежүүд
  const grouped = useMemo(() => {
    const out = [];
    let day = null;
    for (const m of convo.messages) {
      const d = fmtDay(m.at);
      if (d !== day) { out.push({ divider: d, key: `d-${d}-${m._id}` }); day = d; }
      out.push({ msg: m, key: String(m._id) });
    }
    return out;
  }, [convo.messages]);

  return (
    <Offcanvas
      isOpen={isOpen}
      toggle={toggle}
      direction="end"
      className="offcanvas-end border-0"
      style={{ width: 400, maxWidth: "100vw" }}
    >
      {/* ── Толгой ── */}
      <div className="d-flex align-items-center gap-2 p-3 border-bottom bg-soft-primary">
        {active ? (
          <React.Fragment>
            <button
              type="button"
              className="btn btn-sm btn-ghost-secondary rounded-circle"
              onClick={() => { setActive(null); setError(""); setConfirmClear(false); }}
              title="Буцах"
            >
              <i className="ri-arrow-left-line fs-16"></i>
            </button>
            <Avatar user={convo.user || active} />
            <div className="flex-grow-1 min-w-0">
              <div className="fw-semibold text-truncate lh-1">{(convo.user || active).name}</div>
              {active.isGroup ? (
                <small className="text-muted">
                  {(convo.user || active).memberCount || 0} гишүүн
                  {(convo.user || {}).online ? ` · ${convo.user.online} онлайн` : ""}
                </small>
              ) : (
                <small className={(convo.user || active).online ? "text-success" : "text-muted"}>
                  {(convo.user || active).online
                    ? "Онлайн"
                    : (convo.user || active).lastSeen
                      ? `Сүүлд ${moment((convo.user || active).lastSeen).fromNow()}`
                      : "Офлайн"}
                </small>
              )}
            </div>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <i className="ri-chat-3-line fs-20 text-primary"></i>
            <div className="flex-grow-1">
              <div className="fw-semibold lh-1">Чат</div>
              <small className="text-muted">
                {contacts.filter((c) => c.online).length} хүн онлайн
              </small>
            </div>
          </React.Fragment>
        )}
        {/* Группын түүхийг цэвэрлэх — зөвхөн SuperAdmin, зөвхөн группд */}
        {active && active.isGroup && isSuperAdmin && (
          confirmClear ? (
            <React.Fragment>
              <button type="button" className="btn btn-sm btn-danger" disabled={clearing} onClick={doClear}>
                {clearing ? <Spinner size="sm" /> : "Устга"}
              </button>
              <button type="button" className="btn btn-sm btn-light" onClick={() => setConfirmClear(false)}>
                Болих
              </button>
            </React.Fragment>
          ) : (
            <button
              type="button"
              className="btn btn-sm btn-ghost-danger rounded-circle"
              onClick={() => setConfirmClear(true)}
              title="Энэ группын БҮХ мессежийг устгах"
            >
              <i className="ri-brush-line fs-16"></i>
            </button>
          )
        )}
        <button type="button" className="btn btn-sm btn-ghost-secondary rounded-circle" onClick={toggle} title="Хаах">
          <i className="ri-close-line fs-16"></i>
        </button>
      </div>

      <OffcanvasBody className="p-0 d-flex flex-column" style={{ minHeight: 0 }}>
        {error && (
          <div className="alert alert-danger rounded-0 mb-0 py-2 px-3 fs-13 d-flex align-items-center gap-2">
            <i className="ri-error-warning-line"></i>
            <span className="flex-grow-1">{error}</span>
            <button type="button" className="btn-close btn-close-sm" onClick={() => setError("")}></button>
          </div>
        )}

        {!active ? (
          /* ── Харилцагчдын жагсаалт ── */
          <React.Fragment>
            <div className="p-2 border-bottom">
              <div className="position-relative">
                <Input
                  type="text"
                  bsSize="sm"
                  className="ps-4"
                  placeholder="Нэр эсвэл байгууллагаар хайх…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
                <i className="ri-search-line position-absolute text-muted"
                  style={{ left: 8, top: "50%", transform: "translateY(-50%)", fontSize: 13 }}></i>
              </div>
            </div>
            <div className="flex-grow-1 overflow-auto">
              {loadingList && contacts.length === 0 ? (
                <div className="text-center py-5"><Spinner size="sm" /></div>
              ) : filtered.length === 0 ? (
                <div className="text-center text-muted py-5 px-3 fs-13">
                  <i className="ri-user-search-line fs-24 d-block mb-2 opacity-50"></i>
                  {q ? "Хайлтад тохирох хүн алга." : "Чатлах хүн алга."}
                </div>
              ) : (
                filtered.map((c) => (
                  <div
                    key={c._id}
                    role="button"
                    onClick={() => openConvo(c)}
                    className="d-flex align-items-center gap-2 px-3 py-2 border-bottom chat-contact-row"
                  >
                    <Avatar user={c} />
                    <div className="flex-grow-1 min-w-0">
                      <div className="d-flex align-items-center gap-1">
                        <span className={`text-truncate ${c.unread ? "fw-bold" : "fw-medium"}`}>{c.name}</span>
                        {c.isGroup && <span className="badge bg-soft-primary text-primary fs-10 flex-shrink-0">групп</span>}
                        {!c.isGroup && c.role === "SuperAdmin" && (
                          <span className="badge bg-soft-danger text-danger fs-10 flex-shrink-0">админ</span>
                        )}
                      </div>
                      <div className="text-muted text-truncate fs-12">
                        {c.lastMessage
                          ? `${c.lastMessage.mine ? "Та: " : (c.isGroup && c.lastMessage.author ? c.lastMessage.author + ": " : "")}${c.lastMessage.text}`
                          : (c.isGroup ? `${c.memberCount} гишүүн` : (c.company || c.designation || "—"))}
                      </div>
                    </div>
                    <div className="text-end flex-shrink-0">
                      {c.lastMessage && (
                        <div className="text-muted fs-11">{fmtShort(c.lastMessage.at)}</div>
                      )}
                      {c.unread > 0 && (
                        <span className="badge bg-danger rounded-pill mt-1">{c.unread}</span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </React.Fragment>
        ) : (
          /* ── Яриа ── */
          <React.Fragment>
            <div ref={bodyRef} onScroll={onScroll} className="flex-grow-1 overflow-auto p-3 bg-light-subtle">
              {loadingConvo && convo.messages.length === 0 ? (
                <div className="text-center py-5"><Spinner size="sm" /></div>
              ) : convo.messages.length === 0 ? (
                <div className="text-center text-muted py-5 fs-13">
                  <i className="ri-chat-smile-2-line fs-24 d-block mb-2 opacity-50"></i>
                  Хараахан мессеж алга. Эхнийхийг нь бичээрэй.
                </div>
              ) : (
                grouped.map((g) =>
                  g.divider ? (
                    <div key={g.key} className="text-center my-3">
                      <span className="badge bg-soft-secondary text-secondary fs-11">{g.divider}</span>
                    </div>
                  ) : (
                    <div key={g.key} className={`d-flex mb-2 ${g.msg.mine ? "justify-content-end" : ""}`}>
                      <div className="d-flex flex-column" style={{ maxWidth: "78%" }}>
                      <div
                        role="button"
                        // ⚠️ Давхар дарж ❤️ тавих товчлол БАЙХГҮЙ: эхний даралт
                        // цэсийг нээж, түүний бүрхүүл хоёр дахь даралтыг барьж
                        // авдаг тул найдваргүй. Зүрх цэсэнд нэг даралтаар байна.
                        onClick={() => !g.msg.pending && setActing(g.msg)}
                        title="Дарж үйлдэл сонгох (зүрх, хариулах, дамжуулах)"
                        className={`chat-bubble px-3 py-2 rounded-3 ${g.msg.mine ? "bg-primary text-white" : "bg-body border"}`}
                        style={{ wordBreak: "break-word" }}>
                        {g.msg.reply && (
                          <div className={`rounded-2 px-2 py-1 mb-1 ${g.msg.mine ? "bg-white bg-opacity-25" : "bg-light"}`}
                            style={{ borderLeft: "3px solid currentColor" }}>
                            <div className="fw-semibold fs-11">{g.msg.reply.author || "—"}</div>
                            <div className="fs-12 text-truncate" style={{ opacity: .85 }}>
                              {g.msg.reply.text || (g.msg.reply.hasFile ? "📎 хавсралт" : "")}
                            </div>
                          </div>
                        )}
                        {/* Группд хэн бичсэн нь заавал харагдана */}
                        {active.isGroup && !g.msg.mine && g.msg.author && (
                          <div className="d-flex align-items-center gap-1 mb-1">
                            <span className="fw-semibold fs-12" style={{ color: authorColor(g.msg.author._id) }}>
                              {g.msg.author.name}
                            </span>
                          </div>
                        )}
                        {g.msg.forwardedFrom && (
                          <div className={`d-flex align-items-center gap-1 fs-11 mb-1 ${g.msg.mine ? "text-white-50" : "text-muted"}`}
                            style={{ borderLeft: "2px solid currentColor", paddingLeft: 6, opacity: .85 }}>
                            <i className="ri-share-forward-line"></i>
                            <span>Дамжуулсан — {g.msg.forwardedFrom}</span>
                          </div>
                        )}
                        {g.msg.text && <ChatText text={g.msg.text} />}
                        {(g.msg.attachments || []).map((a, i) =>
                          a.url || a.localUrl ? (
                            <ChatAttachment key={i} att={a} onPreview={setPreview} />
                          ) : (
                            /* Зураг биш файл илгээгдэж байна */
                            <div key={i} className="d-flex align-items-center gap-2 mt-1 fs-12 opacity-75">
                              <Spinner size="sm" /> {a.name} {a.size ? `(${humanSize(a.size)})` : ""}
                            </div>
                          )
                        )}
                        <div className={`d-flex align-items-center gap-1 justify-content-end mt-1 fs-11 ${g.msg.mine ? "text-white-50" : "text-muted"}`}>
                          {g.msg.failed && <span className="text-danger">илгээгдсэнгүй</span>}
                          {g.msg.pending && <i className="ri-time-line"></i>}
                          <span>{fmtTime(g.msg.at)}</span>
                          {/* Уншсаны тэмдэг: ҮРГЭЛЖ хоёр зөв.
                              уншаагүй → бүдэг, уншсан → тод (цэнхэр) */}
                          {g.msg.mine && !g.msg.pending && !g.msg.failed && (
                            <i
                              className={`ri-check-double-line chat-tick ${g.msg.readAt ? "chat-tick-read" : "chat-tick-sent"}`}
                              title={g.msg.readAt ? `Уншсан — ${fmtTime(g.msg.readAt)}` : "Хүргэгдсэн, хараахан уншаагүй"}
                            ></i>
                          )}
                        </div>
                      </div>
                      {(g.msg.reactions || []).length > 0 && (
                        <div className={`d-flex flex-wrap gap-1 mt-1 ${g.msg.mine ? "justify-content-end" : ""}`}>
                          {g.msg.reactions.map((r) => (
                            <span
                              key={r.emoji}
                              role="button"
                              onClick={() => react(g.msg, r.emoji)}
                              title={(r.names || []).filter(Boolean).join(", ")}
                              className={`chat-react-chip ${r.mine ? "mine" : ""}`}
                            >
                              {r.emoji} {r.count > 1 ? r.count : ""}
                            </span>
                          ))}
                        </div>
                      )}
                      </div>
                    </div>
                  )
                )
              )}
              <div ref={endRef}></div>
            </div>

            {/* Эш татаж буй мессеж */}
            {replyTo && (
              <div className="px-2 pt-2 border-top">
                <div className="d-flex align-items-start gap-2 bg-light rounded p-2">
                  <i className="ri-reply-line text-success mt-1"></i>
                  <div className="flex-grow-1 min-w-0">
                    <div className="fw-semibold fs-12">
                      {replyTo.mine ? "Та" : (convo.user || active).name}
                    </div>
                    <div className="text-muted fs-12 text-truncate">
                      {replyTo.text || ((replyTo.attachments || []).length ? "📎 хавсралт" : "")}
                    </div>
                  </div>
                  <i role="button" className="ri-close-line" onClick={() => setReplyTo(null)}></i>
                </div>
              </div>
            )}

            {/* Илгээхээр хүлээж буй хавсралтууд */}
            {pending.length > 0 && (
              <div className="px-2 pt-2 d-flex flex-wrap gap-2 border-top">
                {pending.map((f, i) => (
                  <div key={i} className="d-flex align-items-center gap-1 border rounded px-2 py-1 bg-body fs-12">
                    <i className={/^image\//.test(f.type) ? "ri-image-line text-primary" : "ri-attachment-2 text-muted"}></i>
                    <span className="text-truncate" style={{ maxWidth: 120 }}>{f.name}</span>
                    <span className="text-muted">{humanSize(f.size)}</span>
                    <i role="button" className="ri-close-line ms-1"
                      onClick={() => setPending((p) => p.filter((_, j) => j !== i))}></i>
                  </div>
                ))}
              </div>
            )}

            {showEmoji && (
              <div className="border-top" style={{ height: 320 }}>
                <EmojiPicker
                  width="100%"
                  height={320}
                  searchPlaceholder="Emoji хайх…"
                  previewConfig={{ showPreview: false }}
                  onEmojiClick={(e) => setDraft((d) => d + e.emoji)}
                />
              </div>
            )}

            <div className="p-2 border-top d-flex align-items-end gap-1">
              <button
                type="button"
                className={`btn btn-icon flex-shrink-0 ${showEmoji ? "btn-primary" : "btn-ghost-secondary"}`}
                onClick={() => setShowEmoji((v) => !v)}
                title="Emoji"
              >
                <i className="ri-emotion-happy-line"></i>
              </button>
              <button
                type="button"
                className="btn btn-ghost-secondary btn-icon flex-shrink-0"
                onClick={() => fileRef.current && fileRef.current.click()}
                title="Файл хавсаргах (зураг, баримт)"
              >
                <i className="ri-attachment-2"></i>
              </button>
              <input
                ref={fileRef}
                type="file"
                multiple
                className="d-none"
                onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }}
              />
              <Input
                type="textarea"
                rows={1}
                placeholder="Мессеж бичих…  (Enter — илгээх)"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={onKeyDown}
                onPaste={onPaste}
                style={{ resize: "none", maxHeight: 120 }}
              />
              <button
                type="button"
                className="btn btn-primary btn-icon flex-shrink-0"
                onClick={send}
                disabled={(!draft.trim() && pending.length === 0) || sending}
                title="Илгээх"
              >
                {uploading ? <Spinner size="sm" /> : <i className="ri-send-plane-2-fill"></i>}
              </button>
            </div>
          </React.Fragment>
        )}
      </OffcanvasBody>

      {/* ── Мессежийн үйлдлийн хуудас ── */}
      {acting && (
        <div
          role="button"
          onClick={() => setActing(null)}
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-end"
          style={{ background: "rgba(0,0,0,.35)", zIndex: 1100 }}
        >
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            className="w-100 bg-body rounded-top shadow-lg p-2"
          >
            <div className="px-2 pt-1 pb-2 border-bottom mb-1">
              <div className="text-muted fs-11 text-uppercase">Мессеж</div>
              <div className="text-truncate fs-13">
                {acting.text || (acting.attachments || []).map((a) => a.name).join(", ") || "—"}
              </div>
            </div>

            {/* Хариу үйлдэл */}
            <div className="d-flex justify-content-around px-2 py-2 mb-1 border-bottom">
              {REACTIONS.map((e) => (
                <button
                  key={e}
                  type="button"
                  className="btn btn-ghost-secondary btn-sm rounded-circle chat-react-btn"
                  onClick={() => { react(acting, e); setActing(null); }}
                  title={e}
                >
                  <span style={{ fontSize: 20 }}>{e}</span>
                </button>
              ))}
            </div>

            <button type="button" className="btn btn-ghost-secondary w-100 text-start d-flex align-items-center gap-2"
              onClick={() => { setReplyTo(acting); setActing(null); }}>
              <i className="ri-reply-line fs-16 text-success"></i> Хариулах (эш татах)
            </button>

            <button type="button" className="btn btn-ghost-secondary w-100 text-start d-flex align-items-center gap-2"
              onClick={() => { setFwdFor(acting); setActing(null); }}>
              <i className="ri-share-forward-line fs-16 text-primary"></i> Дамжуулах
            </button>

            {acting.text && (
              <button type="button" className="btn btn-ghost-secondary w-100 text-start d-flex align-items-center gap-2"
                onClick={() => { copyText(acting.text); setActing(null); }}>
                <i className="ri-file-copy-line fs-16 text-info"></i> Текстийг хуулах
              </button>
            )}

            {acting.mine && (
              <button type="button" className="btn btn-ghost-danger w-100 text-start d-flex align-items-center gap-2"
                onClick={() => { removeMessage(acting._id); setActing(null); }}>
                <i className="ri-delete-bin-line fs-16"></i> Устгах
              </button>
            )}

            <button type="button" className="btn btn-light w-100 mt-1" onClick={() => setActing(null)}>
              Болих
            </button>
          </div>
        </div>
      )}

      {/* ── Дамжуулах: хүлээн авагч сонгох ── */}
      {fwdFor && (
        <div
          role="button"
          onClick={() => { setFwdFor(null); setFwdPick([]); setFwdQ(""); }}
          className="position-absolute top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center p-3"
          style={{ background: "rgba(0,0,0,.45)", zIndex: 1200 }}
        >
          <div
            role="presentation"
            onClick={(e) => e.stopPropagation()}
            className="bg-body rounded shadow-lg w-100 d-flex flex-column"
            style={{ maxHeight: "80%" }}
          >
            <div className="p-3 border-bottom">
              <div className="fw-semibold mb-1">
                <i className="ri-share-forward-line text-primary me-1"></i>Хэн рүү дамжуулах вэ?
              </div>
              <div className="text-muted fs-12 text-truncate">
                {fwdFor.text || (fwdFor.attachments || []).map((a) => a.name).join(", ")}
              </div>
              <Input
                type="text"
                bsSize="sm"
                className="mt-2"
                placeholder="Хайх…"
                value={fwdQ}
                onChange={(e) => setFwdQ(e.target.value)}
              />
            </div>

            <div className="flex-grow-1 overflow-auto">
              {contacts
                // Групп руу дамжуулах нь сервэрт дэмжигдээгүй тул сонголтоос хасна
                .filter((c) => !c.isGroup)
                .filter((c) => {
                  const t = fwdQ.trim().toLowerCase();
                  return !t || String(c.name).toLowerCase().includes(t) || String(c.company).toLowerCase().includes(t);
                })
                .map((c) => {
                  const on = fwdPick.includes(String(c._id));
                  return (
                    <div
                      key={c._id}
                      role="button"
                      onClick={() =>
                        setFwdPick((p) =>
                          on ? p.filter((x) => x !== String(c._id)) : [...p, String(c._id)]
                        )
                      }
                      className={`d-flex align-items-center gap-2 px-3 py-2 border-bottom ${on ? "bg-soft-primary" : ""}`}
                    >
                      <Input type="checkbox" className="form-check-input mt-0 flex-shrink-0" checked={on} readOnly />
                      <Avatar user={c} showDot={false} />
                      <div className="flex-grow-1 min-w-0">
                        <div className="text-truncate fw-medium">{c.name}</div>
                        <div className="text-muted fs-12 text-truncate">{c.company || c.designation || "—"}</div>
                      </div>
                    </div>
                  );
                })}
            </div>

            <div className="p-2 border-top d-flex gap-2">
              <span className="text-muted fs-12 align-self-center me-auto ps-1">
                {fwdPick.length ? `${fwdPick.length} сонгосон` : "Хүлээн авагч сонгоно уу"}
              </span>
              <button type="button" className="btn btn-light"
                onClick={() => { setFwdFor(null); setFwdPick([]); setFwdQ(""); }}>
                Болих
              </button>
              <button type="button" className="btn btn-primary" onClick={doForward} disabled={!fwdPick.length || fwdBusy}>
                {fwdBusy ? <Spinner size="sm" /> : <><i className="ri-send-plane-2-fill me-1"></i>Дамжуулах</>}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Товч мэдэгдэл */}
      {toast && (
        <div className="position-absolute start-50 translate-middle-x badge bg-dark px-3 py-2"
          style={{ bottom: 80, zIndex: 1300 }}>
          {toast}
        </div>
      )}

      {/* Зураг томруулж харах */}
      {preview && (
        <div
          role="button"
          onClick={() => setPreview(null)}
          className="position-fixed top-0 start-0 w-100 h-100 d-flex align-items-center justify-content-center"
          style={{ background: "rgba(0,0,0,.85)", zIndex: 2000 }}
        >
          <img src={preview.objectUrl} alt={preview.name}
            style={{ maxWidth: "92vw", maxHeight: "88vh", objectFit: "contain" }} />
          <button type="button" className="btn btn-light btn-icon rounded-circle position-absolute"
            style={{ top: 16, right: 16 }} onClick={() => setPreview(null)}>
            <i className="ri-close-line"></i>
          </button>
        </div>
      )}
    </Offcanvas>
  );
};

export default ChatDrawer;
